import { useContext, createContext, type FC, useMemo, type ReactNode } from 'react';

import { type Search } from '@vakantiesnl/types';
import { type OfferItem } from '@vakantiesnl/types/src/search';

import {
	useGetDepartureDatePackageOffers,
	useGetFlightAlternativeOffers,
	useGetMealplanOffers,
	useGetRoomTypeOffers,
} from '../../hooks/queries/packageOffers';
import { useAccoOfferStore } from '../../stores/accommodationStore';

type ProviderProps = {
	children: ReactNode;
	accommodation: Search.FullAccommodationItem;
};

type ContextProps = {
	state: {
		// NB: Accommodation is only for use on Vakanties, else use the hook useGetAccommodationByPath
		accommodation: Search.FullAccommodationItem;
		tripsOfferData: TripsOfferData | { loading: true };
		flightData: FlightData;
		roomData: RoomData;
		mealPlanData: MealPlanData;
		priceOverviewData?: PriceOverviewData;
	};
	dispatch: {
		setFlightOffer: (offer: OfferItem | undefined) => void;
		setRoomOffer: (offer: OfferItem | undefined) => void;
		setMealPlanOffer: (offer: OfferItem | undefined) => void;
		setDepartureDateOffer: (offer: OfferItem | undefined) => void;
	};
};

export type TripsOfferData = {
	selected?: Search.OfferItem;
	highlight?: string;
	departureDateOffers: Search.OfferItem[];
	allDepartureDateOffers: Search.OfferItem[];
	loading: false;
	error?: unknown;
	party?: Search.Party;
	min: number;
	max: number;
	cheapest: string;
	cheapestPricePerAdult: number;
} | null;

export type FlightData =
	| { loading: true }
	| {
			departureDateOffer: Search.OfferItem;
			selectedFlightOffer?: Search.OfferItem;
			flights: Search.OfferItem[];
			loading: false;
			error?: unknown;
	  }
	| null;

export type RoomData =
	| { loading: true }
	| {
			selected?: string;
			roomOffers: Search.OfferItem[];
			previousSelectedOffer: Search.OfferItem;
			loading: false;
			error?: unknown;
	  }
	| null;

export type MealPlanData =
	| { loading: true }
	| {
			selected?: string;
			mealPlanOffers: Search.OfferItem[];
			previousSelectedOffer: Search.OfferItem;
			loading: false;
			error?: unknown;
	  }
	| null;

type PriceOverviewData = {
	selected: Search.OfferItem;
	party?: Search.Party;
};

const AccommodationContext = createContext<ContextProps>({} as ContextProps);

export const AccommodationProvider: FC<ProviderProps> = ({ children, accommodation }) => {
	const departureDateOffer = useAccoOfferStore((s) => s.departureDateOffer);
	const isDepartureDateLoading = useAccoOfferStore((s) => s.isDepartureDateLoading);
	const flightsOffer = useAccoOfferStore((s) => s.flightsOffer);
	const roomTypeOffer = useAccoOfferStore((s) => s.roomTypeOffer);
	const mealplanOffer = useAccoOfferStore((s) => s.mealplanOffer);

	const setDepartureDateOffer = useAccoOfferStore((s) => s.setDepartureDateOffer);
	const setFlightsOffer = useAccoOfferStore((s) => s.setFlightsOffer);
	const setMealplanOffer = useAccoOfferStore((s) => s.setMealplanOffer);
	const setRoomTypeOffer = useAccoOfferStore((s) => s.setRoomTypeOffer);

	const { departureDateOffers } = useGetDepartureDatePackageOffers();
	const { isLoadingFlightAlternativeOffers, flightAlternativeOffers } = useGetFlightAlternativeOffers();
	const { isLoadingRoomTypeOffers, roomTypeOffers } = useGetRoomTypeOffers();
	const { isLoadingMealplanOffers, mealplanOffers } = useGetMealplanOffers();

	const tripsOfferData = useMemo((): ContextProps['state']['tripsOfferData'] => {
		if (isDepartureDateLoading) {
			return { loading: true };
		}

		if (!departureDateOffers?.all.length) {
			return null;
		}

		const tripOfferData: TripsOfferData = {
			...departureDateOffers,
			departureDateOffers: departureDateOffers.all,
			allDepartureDateOffers: departureDateOffers.all,
			loading: false,
		};

		return tripOfferData;
	}, [departureDateOffers, isDepartureDateLoading]);

	const flightData = useMemo((): FlightData => {
		if (isLoadingFlightAlternativeOffers) {
			return { loading: true };
		}
		const flights = flightAlternativeOffers?.all;
		if (!flights?.length || isDepartureDateLoading || !departureDateOffer) {
			return null;
		}

		return {
			flights,
			selectedFlightOffer: flightsOffer,
			departureDateOffer: departureDateOffer,
			loading: false,
		};
	}, [
		departureDateOffer,
		flightAlternativeOffers?.all,
		flightsOffer,
		isDepartureDateLoading,
		isLoadingFlightAlternativeOffers,
	]);

	const roomData = useMemo((): RoomData => {
		if (isLoadingRoomTypeOffers) {
			return { loading: true };
		}
		const roomOffers = roomTypeOffers?.all;
		if (!roomOffers?.length || isLoadingFlightAlternativeOffers || !flightsOffer) {
			return null;
		}

		return {
			roomOffers,
			selected: roomTypeOffer?.id,
			previousSelectedOffer: flightsOffer,
			loading: false,
		};
	}, [flightsOffer, isLoadingFlightAlternativeOffers, isLoadingRoomTypeOffers, roomTypeOffers?.all, roomTypeOffer]);

	const mealPlanData = useMemo((): MealPlanData => {
		if (isLoadingMealplanOffers) {
			return { loading: true };
		}

		if (!mealplanOffers?.all?.length || isLoadingRoomTypeOffers || !roomTypeOffer) {
			return null;
		}

		return {
			mealPlanOffers: mealplanOffers?.all,
			selected: mealplanOffer?.id,
			previousSelectedOffer: roomTypeOffer,
			loading: false,
		};
	}, [isLoadingMealplanOffers, isLoadingRoomTypeOffers, mealplanOffers?.all, roomTypeOffer, mealplanOffer]);

	const priceOverviewData = useMemo((): PriceOverviewData | undefined => {
		const data =
			!isLoadingMealplanOffers && mealplanOffer && mealplanOffers
				? {
						selected: mealplanOffer,
						party: mealplanOffers.party,
					}
				: undefined;

		return data;
	}, [isLoadingMealplanOffers, mealplanOffers, mealplanOffer]);

	const value = useMemo(
		() => ({
			state: {
				accommodation,
				tripsOfferData,
				flightData,
				roomData,
				mealPlanData,
				priceOverviewData,
			},
			dispatch: {
				setFlightOffer: setFlightsOffer,
				setMealPlanOffer: setMealplanOffer,
				setRoomOffer: setRoomTypeOffer,
				setDepartureDateOffer: setDepartureDateOffer,
			},
		}),
		[
			accommodation,
			flightData,
			mealPlanData,
			priceOverviewData,
			roomData,
			setFlightsOffer,
			setMealplanOffer,
			setRoomTypeOffer,
			tripsOfferData,
			setDepartureDateOffer,
		],
	);

	return <AccommodationContext.Provider value={value}>{children}</AccommodationContext.Provider>;
};

export const useAccommodationContext = (): ContextProps => useContext(AccommodationContext);
